'use client';

import Box from '@mui/material/Box';
import { type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';

export interface MetricCellProps {
  /** Overline label (auto-uppercased, e.g. "PURCHASE PRICE"). */
  label: ReactNode;
  /** Primary metric value (e.g. "$725,000", "712", "18 months"). */
  value: ReactNode;
  /**
   * Muted helper text shown below the value (e.g. "× rate ÷ 12", "$77,940 escrowed",
   * "4 weighted comps").
   */
  helper?: ReactNode;
  /**
   * Optional adornment rendered after the value on the same row (e.g. a DeltaPill, a status chip,
   * a tiny sparkle icon).
   */
  endAdornment?: ReactNode;
  /**
   * Tone for the value. Default 'inherit' (text.primary). Use sparingly to highlight a single
   * metric — broad use defeats the hierarchy.
   */
  tone?: 'inherit' | 'primary' | 'success' | 'warning' | 'error';
  /** Sx overrides. */
  sx?: SxProps<Theme>;
}

function valueColor(tone: NonNullable<MetricCellProps['tone']>) {
  if (tone === 'inherit') return 'text.primary';
  return `${tone}.main`;
}

export function MetricCell({ label, value, helper, endAdornment, tone = 'inherit', sx }: MetricCellProps) {
  return (
    <Box sx={composeSx({ display: 'flex', flexDirection: 'column', minWidth: 0 }, sx)}>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          letterSpacing: '0.08em',
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.25 }}>
        <Typography
          variant="body1"
          sx={{
            color: valueColor(tone),
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {value}
        </Typography>
        {endAdornment}
      </Box>
      {helper != null && helper !== '' && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mt: 0.25,
            lineHeight: 1.4,
          }}
        >
          {helper}
        </Typography>
      )}
    </Box>
  );
}

MetricCell.displayName = 'MetricCell';

export default MetricCell;
